import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { capitalizeFirstLetter, checkIfHotjarEnable, dynaTraceGetAction, dynaTraceLeaveAction, timeBetweenDates } from '../../utils/helpers';
import history from '../../utils/history';
import * as sinisterCardsActions from '../../redux/actions/sinister-cards';
import { coveragePlans } from '../../data/coveragePlan';
import Modal from '../../components/common/modal/Modal';
import SinisterCard from '../../components/content/SinisterCard/SinisterCard';
import RimacTag from '../../components/common/RimacTag/RimacTag';
import Button from '../../components/common/button/Button';
import SplitLayout from '../../layout/SplitLayout';
import { PAGINAS_BY_STEP } from '../../utils/constants';

const SinisterSelectPage = () => {
  const dispatch = useDispatch();
  const selectedServicesStored = useSelector(
    (state) => state.sinisterCards.sinisterList
  );
  const coveragesList = useSelector((state) => state.coverages.coveragesList);
  const currentCoveragePlan = useSelector(
    (state) => state.coverages.currentPlan
  );
  const currentUser = useSelector((state) => state.sinisterCards.userInfo);
  const currentUserName = `${capitalizeFirstLetter(
    currentUser?.name ?? ''
  )} ${capitalizeFirstLetter(currentUser?.fatherLastName ?? '')}`;
  const [viewStartTime] = useState(new Date());
  const [selectService, setSelectService] = useState(
    selectedServicesStored || []
  );
  const [isModalOpen, setModalOpen] = useState(false);
  const [isMessageVisible, setMessageVisible] = useState(false);
  const [messageWasSeen, setMessageWasSeen] = useState(false);
  const [currentSinisterID, setCurrentSinisterID] = useState(null);
  const isOtherSelected = selectedServicesStored.some(
    (service) => service.code === 'OTRO_SINIESTRO'
  );
  const [otherCaseSelected, setOtherCaseSelected] = useState(isOtherSelected);
  const modalEl = useRef(null);

  useEffect(() => {
    if (!viewStartTime) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    const endTime = new Date();
    const userTotalTime = timeBetweenDates(viewStartTime, endTime);

    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        task: {
          name: 'Tiempo registrado - Detalle Siniestro',
          time: userTotalTime,
        },
        event: 'trackTimeToComplete',
      });
    }
  }, [viewStartTime]);

  const selectItem = (resp) => {
    console.log(resp);
    setOtherCaseSelected(false);

    const isOtherServiceSelected = selectService.some(
      (service) => service.code === 'OTRO_SINIESTRO'
    );

    if (resp.code === 'OTRO_SINIESTRO' && !isOtherServiceSelected) {
      setOtherCaseSelected(true);
      setSelectService([resp]);
      return;
    } else if (resp.code === 'OTRO_SINIESTRO' && isOtherServiceSelected) {
      setSelectService([]);
      return;
    }

    const isOnArrayService = selectService.find(
      (service) => service.sinisterNum === resp.sinisterNum
    );

    const withoutOtherSinister = selectService.filter(
      (service) => service.code !== 'OTRO_SINIESTRO'
    );

    if (isOnArrayService) {
      const filtered = withoutOtherSinister.filter(
        (service) => service.sinisterNum !== resp.sinisterNum
      );
      return setSelectService(filtered);
    }
    setSelectService([...withoutOtherSinister, resp]);
  };

  const onOpenSinisterModal = (e, resp) => {
    e.stopPropagation();
    setCurrentSinisterID(resp.sinisterNum);
    console.log(resp);
    setModalOpen(true);
    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        "action": {
          "group": "Tramites Siniestros - Protección Tarjeta BBVA",
          "category": `Reporta siniestro - ${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())}`,
          "name": "Ver detalle",
          "label": resp.title
        },
        "event": "trackAction"
      });
      // LAST
      window.digitalData.push({
        "virtual": {
          "name": `web:tramites-siniestros:proteccion-tarjeta-${currentCoveragePlan.toLowerCase()}:modal-detalle-cobertura-${resp.title.replace(/ /g,'-')}`
        },
        "event": "trackVirtual"
      });
    }
  };

  const onCloseMessage = () => {
    setMessageVisible(false);
    setMessageWasSeen(true);
    if (checkIfHotjarEnable()) {
      // LAST
      window.digitalData.push({
        "action": {
          "group": "Tramites Siniestros - Protección Tarjeta BBVA",
          "category": `Reporta siniestro - ${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())}`,
          "name": "click",
          "label": "Entendido"
        },
        "event": "trackAction"
      });
    } 
  };

  const onBackHandler = () => {
    dispatch(sinisterCardsActions.reduceStep({plan: currentCoveragePlan}));
    history.replace('/');
  };

  function onContinueHandler() {
    if (selectService.length === 1 && !messageWasSeen && !otherCaseSelected) {
      setMessageVisible(true);
      if (checkIfHotjarEnable()) {
        // LAST
        window.digitalData.push({
          "virtual": {
            "name": `web:tramites-siniestros:proteccion-tarjeta-${currentCoveragePlan.toLowerCase()}:modal-un-siniestro`
          },
          "event": "trackVirtual"
        });
      }
      return;
    }
    if (checkIfHotjarEnable()) {
      let value = '';
      selectService.forEach((sinister, index) => {
        value += (index === 0 ? `${capitalizeFirstLetter(currentCoveragePlan.toLowerCase())}` : '') + ` - ${sinister.title}`
      });
      // LAST
      window.digitalData.push({
        metadata: {
          key: 'Tipo de siniestro - Protección Tarjeta BBVA',
          value,
        },
        event: 'trackMetadata',
      });
    }

    const isOtherBanksCoverageSelected =
      selectService.filter((service) => service.code === 'COB04').length > 0;
    dispatch(sinisterCardsActions.setSinisterList(selectService));

    if (isOtherBanksCoverageSelected) {
      const action = dynaTraceGetAction('load otros seguros page');
      dynaTraceLeaveAction(action);
      dispatch(sinisterCardsActions.setStep({step: PAGINAS_BY_STEP.OTRO_SEGURO.step}));
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/otros-seguros`.replace('//','/'));
    } else if (otherCaseSelected) {
    dispatch(sinisterCardsActions.setStep({step: PAGINAS_BY_STEP.DESCRIPCION_OTRO_SINIESTRO.step}));
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/otro-caso`.replace('//','/'));
    } else {
      dispatch(sinisterCardsActions.setStep({step: PAGINAS_BY_STEP.ANTES_EMPEZAR.step}));
      history.replace(`/${coveragePlans[currentCoveragePlan].uri}/antes-de-comenzar`.replace('//','/'));
    }
  };

  const selectedSinister = coveragesList.find(
    (sin) => sin.sinisterNum === currentSinisterID
  );

  const leftContent = (
    <>
      <RimacTag
        customClasses="u-mb-8"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <div className="titleCont">
        <h1 className="u-mb-40">Seguro de Protección de Tarjetas</h1>
        <p>
          <span className="u-font-bold">{currentUserName}</span>, te estaremos
          apoyando en todo momento para reportar tu siniestro.
        </p>
      </div>
    </>
  );

  const rightContent = (
    <>
      <RimacTag
        customClasses="u-mb-16 mobile"
        color={coveragePlans[currentCoveragePlan].color}
        text={coveragePlans[currentCoveragePlan].name}
      />
      <div className="titleCont">
        <h2 className="u-mb-16">¿Cuéntanos, que te sucedió?</h2>
        <p>
        Puedes elegir más de una opción de siniestro de acuerdo a lo ocurrido. En caso elijas
          <b> "Mi siniestro es otro"</b>, no podrás elegir las demás opciones.
        </p>
      </div>

      <div data-testid="sinisters-container" className="sinisterContainer">
        {coveragesList.map((resp) => {
          const isActive =
            selectService.filter(
              (service) =>
                Number(service.sinisterNum) === Number(resp.sinisterNum)
            ).length > 0;
          return (
            <SinisterCard
              key={`sinisterCard-${resp.sinisterNum}`}
              icon={resp.icon}
              altText={resp.title}
              title={resp.title}
              content={resp.description}
              itemKey={resp.code}
              onLabelClick={() => selectItem(resp)}
              onDetailClick={(e) =>
                onOpenSinisterModal(e, resp)
              }
              isChecked={isActive}
              relativeNum={resp.sinisterNum}
              isNumberActive={false}
              detailsActive={true}
            />
          );
        })}
      </div>
    </>
  );

  return (
    <SplitLayout
      containerClasses="sinisterSelectPage"
      data-testid="sinister-select-page"
      leftClasses="sinisterSelectPage__left"
      rightClasses="sinisterSelectPage__right"
      leftContent={leftContent}
      rightContent={rightContent}
      onNavContinueClick={onContinueHandler}
      onNavBackClick={onBackHandler}
      navCurrentStep={1}
      navTotalSteps={5}
      isNavActive={true}
      isNavNextDisabled={selectService.length > 0 ? false : true}
      navStepperText={'PASO 1 DE 5'}
    >
      <div
        data-testid="sinister-detail-modal"
        className="sinisterModal"
        ref={modalEl}
      >
        <Modal
          onLabelClosed={true}
          visible={isModalOpen}
          onToggle={() => setModalOpen(false)}
          className={isModalOpen ? 'sinisterModalMain--new' : ''}
          onLabelTextClosed=""
          height={ currentSinisterID === '5' ? '50%' :'100%' }
        >
          <div className="sinisterModalMain__info">
            <div className="sinisterModalMain__info-container">
              {currentSinisterID === null ? null : (
                <>
                  <RimacTag
                    customClasses="u-mb-16"
                    color={coveragePlans[currentCoveragePlan].color}
                    text={coveragePlans[currentCoveragePlan].name}
                  />
                  <div className="sinisterModalMain__wrap">
                    <div className="wrap__left">
                      <div className="sinisterModalMain__titleCont">
                        <h1 className="sinisterModalMain__title">
                          {selectedSinister?.title}
                        </h1>
                        <img
                          className="imgMobile"
                          src={selectedSinister?.icon}
                          alt={selectedSinister?.title}
                        />
                      </div>
                      {
                        typeof selectedSinister?.innerModalInfo === 'string' ? (
                        <div dangerouslySetInnerHTML={{
                          __html: selectedSinister?.innerModalInfo,
                        }}></div>
                        ) : 
                        (<div>{selectedSinister?.innerModalInfo} </div>)
                      }
                    </div>
                    <div className="wrap__right">
                      <img
                        className="imgDesktop"
                        src={selectedSinister?.icon}
                        alt={selectedSinister?.title}
                      />
                    </div>
                  </div>
                  <div className="sinisterModalMain__info-content">
                    {
                      typeof selectedSinister?.modalInfo === 'string' ? (
                        <div dangerouslySetInnerHTML={{
                          __html: selectedSinister?.modalInfo,
                        }}></div>
                      ) :
                        (<div>{selectedSinister?.modalInfo} </div>)
                    }
                  </div>
                </>
              )}
            </div>
            <div className="sinisterModalMain__info-shadow"></div>
          </div>
        </Modal>
      </div>

      <div data-testid="sinister-msg-modal" className="sinisterModal">
        <Modal
          onLabelClosed={true}
          visible={isMessageVisible}
          onToggle={onCloseMessage}
          className={isMessageVisible ? 'sinisterModalMain--msg' : ''}
          onLabelTextClosed=""
        >
          <div className="innerModal">
            <p>¡Puedes reportar más de 1 siniestro, si así lo necesitas!</p>
            <Button primary onClick={onCloseMessage}>
              Entendido
            </Button>
          </div>
          <div className="sinisterModalMain__info-shadow"></div>
        </Modal>
      </div>
    </SplitLayout>
  );
};

export default SinisterSelectPage;
